import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  GithubAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  AuthProvider,
  UserCredential,
  updateProfile,
  User as FirebaseUser,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
} from 'firebase/firestore';

import { auth, db } from './firebaseConfig';
import { preferRedirectSignIn } from '../lib/platform';
import { User, UserRole, SignUpCredentials, SignInCredentials, OAuthProvider, FirestoreUser } from '../domain';

// Popup errors that mean "the popup itself couldn't run" — safe to retry the
// same sign-in via redirect. Deliberately excludes auth/popup-closed-by-user
// and auth/cancelled-popup-request, which reflect user intent, not a failure.
const POPUP_UNAVAILABLE_CODES = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

/** Set while a signInWithRedirect is away, so the start it comes back to knows to wait for its
 * result before taking the session it brings (see awaitSignInCheck). */
const REDIRECT_FLAG = 'choreschamp.oauthRedirect';

/** What the server's beforeUserCreated says when it refuses to create an account
 * (functions/src/services/account-gate-service.ts); Firebase passes it on inside its own error. */
const ACCOUNT_NEEDS_CODE = 'ACCOUNT_NEEDS_CODE';

/** The server's refusal to create an account, as the AccountNotAllowedError the phone's own check
 * throws; anything else as it was. */
const asRefusal = (error: unknown): unknown =>
  String((error as { message?: unknown })?.message ?? '').includes(ACCOUNT_NEEDS_CODE) ? new AccountNotAllowedError() : error;

/** A sign-in that would have created an account without a code, refused by the server's
 * beforeUserCreated before the account exists (docs/plans/local-mode.md, Phase 3). */
export class AccountNotAllowedError extends Error {
  constructor() {
    super('There is no account for this sign-in yet, and creating one takes a code.');
    this.name = 'AccountNotAllowedError';
  }
}

class AuthService {
  /** The OAuth sign-in under way, settled once it's been let in or turned away. */
  private signInCheck: Promise<unknown> | null = null;
  /** The result of a redirect sign-in coming back, asked for once per page load. */
  private redirectResult: Promise<User | null> | null = null;
  /** The uid of the last sign-in let in by this page, as against one only heard of from another
   * tab (see admittedHere). */
  private admittedUid: string | null = null;

  async signUp(credentials: SignUpCredentials): Promise<User> {
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      const firebaseUser = result.user;

      if (credentials.displayName) {
        await updateProfile(firebaseUser, {
          displayName: credentials.displayName,
        });
      }

      const firestoreUser: FirestoreUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || credentials.email,
        displayName: credentials.displayName || firebaseUser.displayName || undefined,
        photoURL: firebaseUser.photoURL || undefined,
        createdAt: new Date().toISOString(),
        lastSignIn: new Date().toISOString(),
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), firestoreUser);

      // onNewUserSetup cloud function sets roles asynchronously; default to Viewer until token refreshes
      return this.mapFirebaseUserToUser(firebaseUser, [UserRole.Viewer]);
    } catch (error) {
      this.handleAuthError(error);
      throw asRefusal(error);
    }
  }

  async signIn(credentials: SignInCredentials): Promise<User> {
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password
      );

      const firebaseUser = result.user;
      const roles = await this.fetchRolesFromToken(firebaseUser);

      await this.updateLastSignIn(firebaseUser.uid);

      return this.mapFirebaseUserToUser(firebaseUser, roles);
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  /**
   * Hybrid OAuth sign-in:
   *  - mobile browsers / installed PWAs → full-page redirect (popups are
   *    blocked or open a detached window that can't return the credential);
   *  - desktop browsers → popup, falling back to redirect if the popup is
   *    blocked despite the environment check.
   *
   * On the redirect path this navigates away and never resolves; the returning
   * session is drained by getOAuthRedirectResult() at app boot, and its errors
   * surface there.
   */
  async signInWithOAuth(provider: OAuthProvider): Promise<User | null> {
    const authProvider = this.buildOAuthProvider(provider);
    const redirect = async () => {
      sessionStorage.setItem(REDIRECT_FLAG, '1');
      await signInWithRedirect(auth, authProvider);
      // Navigation is under way; nothing meaningful to return.
      return null;
    };

    const attempt = (async () => {
      if (preferRedirectSignIn()) return redirect();

      let result;
      try {
        result = await signInWithPopup(auth, authProvider);
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (code && POPUP_UNAVAILABLE_CODES.has(code)) return redirect();
        throw asRefusal(error);
      }
      return this.admit(result);
    })();

    // Set before the popup can resolve, so the onAuthStateChanged it causes finds it and waits.
    const check = attempt.catch(() => undefined);
    this.signInCheck = check;
    try {
      return await attempt;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    } finally {
      if (this.signInCheck === check) this.signInCheck = null;
    }
  }

  /**
   * Completes a pending signInWithRedirect when the app reboots after a
   * mobile/PWA OAuth redirect. Returns the signed-in user, or null when there
   * was no redirect in flight. onAuthStateChanged establishes the session
   * itself; this exists so a new user's Firestore doc is provisioned and the
   * redirect leg's errors — the server refusing an account among them — can
   * surface. Asked for once per page load, by whichever caller comes first.
   */
  getOAuthRedirectResult(): Promise<User | null> {
    this.redirectResult ??= (async () => {
      try {
        let result;
        try {
          result = await getRedirectResult(auth);
        } catch (error) {
          throw asRefusal(error);
        }
        return result?.user ? await this.admit(result) : null;
      } finally {
        sessionStorage.removeItem(REDIRECT_FLAG);
      }
    })();
    return this.redirectResult;
  }

  /**
   * Resolves once any sign-in under way, by popup or coming back from a redirect, has been
   * completed: its user doc provisioned and admittedHere set. onAuthStateChanged reports an account
   * the moment it exists; whoever acts on a signed-in user must wait for this first.
   */
  async awaitSignInCheck(): Promise<void> {
    if (sessionStorage.getItem(REDIRECT_FLAG)) {
      await this.getOAuthRedirectResult().catch(() => undefined);
    }
    if (this.signInCheck) await this.signInCheck;
  }

  /** Whether this page itself let `uid`'s sign-in in. Another tab's sign-in reaches this one through
   * onAuthStateChanged too, before that tab has decided whether to keep it; only the tab deciding
   * knows, so only it may act on the decision (carrying local data into the account). */
  admittedHere(uid: string): boolean {
    return this.admittedUid === uid;
  }

  /** The uid signed in now, if any: after awaitSignInCheck, whether the user reported is still the
   * one signed in, or was turned away meanwhile. */
  currentUid(): string | null {
    return auth.currentUser?.uid ?? null;
  }

  /**
   * Completes a sign-in: notes it as this page's (admittedHere) and provisions its user doc. Who
   * may create an account was decided by the server before the account existed (beforeUserCreated),
   * so any account that reaches here is one to let in: an existing one, or a new one whose email a
   * code was redeemed for.
   */
  private async admit(result: UserCredential): Promise<User> {
    this.admittedUid = result.user.uid;
    return this.provisionOAuthUser(result.user);
  }

  private buildOAuthProvider(provider: OAuthProvider): AuthProvider {
    if (provider === 'google') {
      const authProvider = new GoogleAuthProvider();
      authProvider.addScope('profile');
      authProvider.addScope('email');
      return authProvider;
    }
    if (provider === 'github') {
      const authProvider = new GithubAuthProvider();
      authProvider.addScope('user:email');
      return authProvider;
    }
    throw new Error(`Unsupported OAuth provider: ${provider}`);
  }

  /**
   * Ensures the signed-in OAuth user has a Firestore user doc (creating it on
   * first sign-in, refreshing lastSignIn otherwise), then maps it with roles.
   * Shared by the popup and redirect completion paths.
   */
  private async provisionOAuthUser(firebaseUser: FirebaseUser): Promise<User> {
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      const firestoreUser: FirestoreUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || undefined,
        photoURL: firebaseUser.photoURL || undefined,
        createdAt: new Date().toISOString(),
        lastSignIn: new Date().toISOString(),
      };

      await setDoc(userDocRef, firestoreUser);
    } else {
      await this.updateLastSignIn(firebaseUser.uid);
    }

    const roles = await this.fetchRolesFromToken(firebaseUser);
    return this.mapFirebaseUserToUser(firebaseUser, roles);
  }

  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const firebaseUser = auth.currentUser;

    if (!firebaseUser) {
      return null;
    }

    const roles = await this.fetchRolesFromToken(firebaseUser);
    return this.mapFirebaseUserToUser(firebaseUser, roles);
  }

  /**
   * Reads roles from the Firebase ID token's custom claims.
   * Handles both a `roles: string[]` claim and a legacy `admin: boolean` claim.
   * Falls back to [Viewer] when no claims are present (e.g. before onNewUserSetup runs).
   */
  private async fetchRolesFromToken(firebaseUser: FirebaseUser): Promise<UserRole[]> {
    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      const claims = tokenResult.claims as Record<string, unknown>;
      const roles: UserRole[] = [];

      if (claims.admin === true && !roles.includes(UserRole.Admin)) {
        roles.push(UserRole.Admin);
      }

      if (Array.isArray(claims.roles)) {
        for (const claimRole of claims.roles as string[]) {
          if (claimRole === UserRole.Admin && !roles.includes(UserRole.Admin)) {
            roles.push(UserRole.Admin);
          } else if (claimRole === UserRole.User && !roles.includes(UserRole.User)) {
            roles.push(UserRole.User);
          } else if (claimRole === UserRole.Viewer && !roles.includes(UserRole.Viewer)) {
            roles.push(UserRole.Viewer);
          } else if (claimRole === 'Basic' && !roles.includes(UserRole.User)) {
            // Legacy claim name used before the rename
            roles.push(UserRole.User);
          }
        }
      }

      return roles.length > 0 ? roles : [UserRole.Viewer];
    } catch (error) {
      console.error('Error reading roles from token:', error);
      return [UserRole.Viewer];
    }
  }

  private async updateLastSignIn(uid: string): Promise<void> {
    try {
      const userDocRef = doc(db, 'users', uid);
      await setDoc(
        userDocRef,
        { lastSignIn: new Date().toISOString() },
        { merge: true }
      );
    } catch (error) {
      console.error('Error updating last sign in:', error);
    }
  }

  private mapFirebaseUserToUser(
    firebaseUser: FirebaseUser,
    roles: UserRole[]
  ): User {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || undefined,
      photoURL: firebaseUser.photoURL || undefined,
      roles,
      createdAt: firebaseUser.metadata.creationTime
        ? new Date(firebaseUser.metadata.creationTime)
        : new Date(),
      lastSignIn: firebaseUser.metadata.lastSignInTime
        ? new Date(firebaseUser.metadata.lastSignInTime)
        : new Date(),
    };
  }

  onAuthStateChanged(
    callback: (user: User | null) => void,
    onError?: (error: Error) => void
  ): () => void {
    return onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        try {
          if (firebaseUser) {
            const user = await this.getCurrentUser();
            callback(user);
          } else {
            callback(null);
          }
        } catch (error) {
          if (onError) {
            onError(error instanceof Error ? error : new Error('Auth state change error'));
          }
        }
      }
    );
  }

  private handleAuthError(error: unknown): void {
    const authError = error as { code?: string; message?: string };

    const errorMessages: Record<string, string> = {
      'auth/user-not-found': 'No user found with this email address.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/user-disabled': 'This user account has been disabled.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/popup-closed-by-user': 'Sign in popup was closed.',
      'auth/popup-blocked': 'Sign in popup was blocked by the browser.',
      'auth/account-exists-with-different-credential':
        'An account already exists with the same email address.',
    };

    const message =
      errorMessages[authError.code || ''] ||
      authError.message ||
      'An authentication error occurred.';

    console.error(`[Auth Error] ${authError.code}: ${message}`);
  }
}

export const authService = new AuthService();
export default authService;
